/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: partner-due-date-templates-searchview-component.ts
 *
 * Created by randysorensen on 5/21/18.
 */

function PartnerDueDateTemplatesSearchViewController(
    this: JS5Object,
    _: JS5Object,
    uiGridConstants: JS5Object,
    $location: JS5Object,
    ModalService: JS5Object,
    NavbarUtilService: JS5Object,
    NotificationService: JS5Object,
    PartnerEditService: JS5Object
) {

    const queryForGridData = (): void => {
        PartnerEditService.getDueDateTemplatesSearchViewData(this.showHidden)
            .then((dueDateTemplatesData: JS5Object) => {
                this.gridOptions.data = dueDateTemplatesData.dueDateTemplates;
                this.partnerObject = dueDateTemplatesData.partnerObject;
                this.searchText = _.get($location.search(), 'searchText') || null;
                filterGrid(this.searchText);
            }).finally(() => {
                this.loadingIndicator.show = false;
            });
    };

    const getSelectedCount = (): number => {
        let selectedCount = 0;
        if (_.get(this, 'gridApi.selection')) {
            selectedCount = this.gridApi.selection.getSelectedCount();
        }
        return selectedCount;
    };

    const isSelectedRowHidden = (): boolean => {
        //
        // Equivalent to:
        //
        //  return this.gridApi.selection.getSelectedRows()[0].hidden;
        //
        return _.get(_.invoke(this, 'gridApi.selection.getSelectedRows'), '[0].hidden');
    };

    const editDueDateTemplate = (dueDateTemplate: JS5Object): void => {
        if (_.get(dueDateTemplate, 'hidden')) {
            NotificationService.createWarningNotification('You cannot edit a hidden SLA template.');
        } else {
            let dueDateTemplateId = _.get(dueDateTemplate, 'id') || -1;
            $location.url(`/system/partners/edit/${this.partnerObject.id}/dueDateTemplates/edit/${dueDateTemplateId}`);
        }
    };

    const cloneSelectedDueDateTemplate = (): void => {
        const selectedDueDateTemplate = this.gridApi.selection.getSelectedRows()[0];
        PartnerEditService.cloneDueDateTemplate(selectedDueDateTemplate)
            .then(() => {
                queryForGridData();
                NotificationService.createSuccessNotification('SLA template successfully cloned.');
            });
    };

    const toggleSelectedDueDateTemplateVisibility = (): void => {
        const selectedDueDateTemplate = this.gridApi.selection.getSelectedRows()[0];
        PartnerEditService.toggleDueDateTemplateVisibility(selectedDueDateTemplate)
            .finally(() => {
                queryForGridData();
            });
    };

    const showConfirmationModal = (action: string, handler: JS5Function): void => {
        let capitalizedAction: string = `${action[0].toUpperCase()}${action.slice(1)}`;
        ModalService.showMessageModal({
                messageText: `Are you sure you want to ${action} the selected SLA template?`,
                title: capitalizedAction + ' SLA Template',
                windowClass: 'confirm-modal-sm',
                showOK: true,
                okText: 'Yes',
                pullCancelLeft: true
            },
            handler
        );
    };

    this.onRowDblClick = (row: JS5Object): void => {
        editDueDateTemplate(row.entity);
    };

    const filterGrid = (searchText: string): void => {
        if (!_.isNil(searchText)) {
            this.searchText = searchText.toLowerCase();
            this.gridApi.grid.refresh();
        }
    };

    this.processGridFilter = (rows: JS5Object[]): JS5Object[] => {
        _.forEach(rows, (row: JS5Object) => {
            row.visible = _.size(this.searchText) < 1 || _.includes(row.entity.name.toLowerCase(), this.searchText);
        });
        return rows;
    };

    const initGrid = (): void => {
        this.gridOptions = {
            rowSelection: true,
            enableFullRowSelection: true,
            enableRowHeaderSelection: false,
            enableSelectAll: false,
            multiSelect: false,
            enableColumnResizing: true,
            enableSorting: true,
            enableColumnMenus: false,
            enableHorizontalScrollbar: uiGridConstants.ALWAYS,
            enableVerticalScrollbar: uiGridConstants.ALWAYS,
            rowHeight: 26,
            rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
            onRegisterApi: (gridApi: JS5Object): void => {
                this.gridApi = gridApi;
                this.gridApi.grid.registerRowsProcessor(this.processGridFilter, 200);
            },
            data: []
        };

        this.gridOptions.columnDefs = [
            {
                field: 'name',
                type: 'string',
                width: '*',
                sort: {
                    direction: 'asc',
                    priority: 0
                },
                displayName: 'Name',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'expression',
                type: 'string',
                width: '*',
                sort: {
                    direction: 'asc',
                    priority: 3
                },
                displayName: 'Expression',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'warningThresholdHours',
                type: 'number',
                width: '15%',
                sort: {
                    direction: 'asc',
                    priority: 1
                },
                displayName: 'Warn Hours Before',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'business.name',
                type: 'string',
                width: '15%',
                sort: {
                    direction: 'asc',
                    priority: 2
                },
                displayName: 'Operational Hour Set',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            }];
    };

    const initToolbar = (): void => {
        this.toolbarButtonDefs = [
            {
                label: 'New',
                enabled: (): boolean => this.canWrite,
                onClick: (): void => editDueDateTemplate(null)
            },
            {
                label: 'Edit',
                enabled: (): boolean => getSelectedCount() === 1 && !isSelectedRowHidden(),
                onClick: (): void => editDueDateTemplate(this.gridApi.selection.getSelectedRows()[0])
            },
            {
                label: 'Clone',
                enabled: (): boolean => this.canWrite && getSelectedCount() === 1 && !isSelectedRowHidden(),
                onClick: (): void => showConfirmationModal('clone', cloneSelectedDueDateTemplate)
            },
            {
                label: 'Hide',
                enabled: (): boolean => this.canWrite && getSelectedCount() === 1 && !isSelectedRowHidden(),
                onClick: (): void => showConfirmationModal('hide', toggleSelectedDueDateTemplateVisibility)
            },
            {
                label: 'Unhide',
                enabled: (): boolean => this.canWrite && getSelectedCount() === 1 && isSelectedRowHidden(),
                onClick: (): void => showConfirmationModal('unhide', toggleSelectedDueDateTemplateVisibility)
            }];

        this.toolbarCheckboxDefs = [
            {
                label: 'Show Hidden',
                onUpdate: (newValue: boolean): void => {
                    this.showHidden = newValue;
                    queryForGridData();
                },
                isChecked: this.showHidden
            }];
    };

    this.$onInit = (): void => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        this.gridApi = null;
        this.searchText = null;
        this.gridOptions = {};

        this.showHidden = false;

        initGrid();
        initToolbar();

        if (this.canRead) {
            queryForGridData();

            NavbarUtilService.viewSearchFunction = queryForGridData;
        } else {
            NavbarUtilService.viewSearchFunction = () => {};
        }
    };

}

angular.module('prism-ui.partner').component(
    'partnerDueDateTemplatesSearchView',
    {
        controller: PartnerDueDateTemplatesSearchViewController,
        templateUrl: 'system/partner/editor/components/view/dueDateTemplates/searchView/partner-due-date-templates-searchview-template.html',
        bindings: {
            canRead: '<',
            canWrite: '<',
            partnerObject: '<'
        }
    });
